import React, { Fragment, useEffect, useState } from "react";
import { Card, CardBody, Col, Container, Row, Table, Badge } from "reactstrap";
import { useDispatch } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import Breadcrumbs from "../../../layout/breadcrumb";
import PreLoader from "../../../share-components/ui/preLoader";
import withReducer from "../../../store/withReducer";
import orderReducer from "../store";
import { getGojungoOrder } from "../store/orderSlice";

const dataMenu = [
  { type: 1, text: "Gojungo Orders", link: `${process.env.PUBLIC_URL}/order/gojungo-orders` },
  { type: 0, text: "Order View" },
];

function GojungoOrderView() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    dispatch(getGojungoOrder(id)).then((res) => {
      if (res.payload) setOrder(res.payload);
      setLoading(false);
    });
  }, [id]);

  if (loading) {
    return (
      <Fragment>
        <Breadcrumbs title="Order View" data={dataMenu} />
        <Container fluid>
          <div style={{ minHeight: 400 }}><PreLoader /></div>
        </Container>
      </Fragment>
    );
  }

  if (!order) {
    return (
      <Fragment>
        <Breadcrumbs title="Order View" data={dataMenu} />
        <Container fluid>
          <p className="text-danger mt-4">Order not found.</p>
        </Container>
      </Fragment>
    );
  }

  const shippingAddr = order.shipping_address || {};
  const billingAddr  = order.billing_address  || {};
  const rate = parseFloat(order.currency_rate || 1);
  const toGBP = (val) => (parseFloat(val || 0) / rate).toFixed(2);

  return (
    <Fragment>
      <Breadcrumbs title={`Order: ${order.order_code}`} data={dataMenu} />

      <Container fluid className="product-wrapper">

        {/* Order Summary */}
        <Card className="mb-3">
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Order Details</h5>
              <button className="btn btn-sm btn-light" onClick={() => history.goBack()}>← Back</button>
            </div>
            <Row>
              <Col md={3}><small className="text-muted">Order Code</small><p className="fw-bold mb-1">{order.order_code}</p></Col>
              <Col md={3}><small className="text-muted">Channel No</small><p className="mb-1">{order.order_channel_no || '—'}</p></Col>
              <Col md={3}><small className="text-muted">Order Date</small><p className="mb-1">{order.order_date}</p></Col>
              <Col md={3}><small className="text-muted">Channel / Tenant</small><p className="mb-1">{order.channel?.toUpperCase()} / {order.tenant?.toUpperCase()}</p></Col>
              <Col md={3}><small className="text-muted">Currency</small><p className="mb-1">{order.currency}</p></Col>
              <Col md={3}><small className="text-muted">Currency Rate</small><p className="mb-1">{order.currency_rate}</p></Col>
              <Col md={3}><small className="text-muted">Total Price</small><p className="mb-1">GBP {toGBP(order.total_price)}</p></Col>
              <Col md={3}><small className="text-muted">Ship Country</small><p className="mb-1">{order.ship_country_code}</p></Col>
              <Col md={3}><small className="text-muted">Shipping Charges</small><p className="mb-1">GBP {toGBP(order.shipping_charges)}</p></Col>
              <Col md={3}><small className="text-muted">Shipping Discount</small><p className="mb-1">GBP {toGBP(order.shipping_discount)}</p></Col>
              <Col md={3}><small className="text-muted">Fulfillment</small><p className="mb-1">{order.fulfillmentType}</p></Col>
              <Col md={3}><small className="text-muted">Status</small><p className="mb-1">
                <Badge color={order.is_confirmed ? 'success' : 'warning'}>{order.is_confirmed ? 'Confirmed' : 'Pending'}</Badge>
                {order.is_test_order && <Badge color="secondary" className="ms-1">Test</Badge>}
              </p></Col>
            </Row>
          </CardBody>
        </Card>

        {/* Customer / Addresses */}
        <Row className="mb-3">
          <Col md={4}>
            <Card className="h-100">
              <CardBody>
                <h6 className="mb-3">Customer</h6>
                {order.customer ? (
                  <>
                    <p className="mb-1">{order.customer.firstName} {order.customer.lastName}</p>
                    <p className="mb-1 text-muted">{order.customer.email}</p>
                    <p className="mb-0 text-muted">{order.customer.phoneMobile}</p>
                  </>
                ) : <p className="text-muted">—</p>}
              </CardBody>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="h-100">
              <CardBody>
                <h6 className="mb-3">Shipping Address</h6>
                {shippingAddr.firstName ? (
                  <>
                    <p className="mb-1">{shippingAddr.firstName} {shippingAddr.lastName}</p>
                    <p className="mb-1">{shippingAddr.streetNo}{shippingAddr.streetExtension ? ` ${shippingAddr.streetExtension}` : ''}</p>
                    <p className="mb-1">{shippingAddr.zip} {shippingAddr.city}</p>
                    <p className="mb-0">{shippingAddr.country}</p>
                  </>
                ) : <p className="text-muted">—</p>}
              </CardBody>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="h-100">
              <CardBody>
                <h6 className="mb-3">Billing Address</h6>
                {billingAddr.firstName ? (
                  <>
                    <p className="mb-1">{billingAddr.firstName} {billingAddr.lastName}</p>
                    <p className="mb-1">{billingAddr.streetNo}{billingAddr.streetExtension ? ` ${billingAddr.streetExtension}` : ''}</p>
                    <p className="mb-1">{billingAddr.zip} {billingAddr.city}</p>
                    <p className="mb-0">{billingAddr.country}</p>
                  </>
                ) : <p className="text-muted">—</p>}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Line Items */}
        <Card className="mb-3">
          <CardBody>
            <h6 className="mb-3">Line Items ({order.line_items?.length || 0})</h6>
            <div className="table-responsive">
              <Table bordered size="sm">
                <thead className="table-light">
                  <tr>
                    <th>SKU</th>
                    <th>Gojungo SKU</th>
                    <th>Product</th>
                    <th>Qty</th>
                    <th>Price (GBP)</th>
                    <th>Discount (GBP)</th>
                    <th>Total (GBP)</th>
                  </tr>
                </thead>
                <tbody>
                  {order.line_items?.length ? order.line_items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.sku || <span className="text-danger fw-bold">Not Found</span>}</td>
                      <td>{item.gojungo_sku}</td>
                      <td>{item.billing_text}</td>
                      <td>{item.quantity}</td>
                      <td>{toGBP(item.price)}</td>
                      <td>{toGBP(item.discountedPrice)}</td>
                      <td>{(parseFloat(item.price || 0) * parseInt(item.quantity || 0) / rate).toFixed(2)}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan={7} className="text-center text-muted">No line items</td></tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* Totals summary */}
            {(() => {
              const items = order.line_items || [];
              const total = items.reduce((sum, i) => sum + parseFloat(i.price || 0) * parseInt(i.quantity || 0), 0) / rate;
              const totalDiscount = items.reduce((sum, i) => sum + parseFloat(i.discountedPrice || 0), 0) / rate;
              const shipping = parseFloat(order.shipping_charges || 0) / rate;
              const grandTotal = total - totalDiscount + shipping;
              return (
                <div className="d-flex justify-content-end mt-3">
                  <table style={{ minWidth: 280 }}>
                    <tbody>
                      <tr>
                        <td className="text-muted pe-4">Total</td>
                        <td className="text-end fw-bold">GBP {total.toFixed(2)}</td>
                      </tr>
                      <tr>
                        <td className="text-muted pe-4">Total Discount</td>
                        <td className="text-end fw-bold">- GBP {totalDiscount.toFixed(2)}</td>
                      </tr>
                      <tr>
                        <td className="text-muted pe-4">Shipping Charges</td>
                        <td className="text-end fw-bold">GBP {shipping.toFixed(2)}</td>
                      </tr>
                      <tr className="border-top">
                        <td className="text-muted pe-4 pt-2"><strong>Grand Total</strong></td>
                        <td className="text-end fw-bold pt-2">GBP {grandTotal.toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </CardBody>
        </Card>

        {/* Refunds */}
        {order.refunds?.length > 0 && (
          <Card className="mb-3">
            <CardBody>
              <h6 className="mb-3">Refunds ({order.refunds.length})</h6>
              <div className="table-responsive">
                <Table bordered size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>SKU</th>
                      <th>Product</th>
                      <th>Qty</th>
                      <th>Return Total (GBP)</th>
                      <th>Discount (GBP)</th>
                      <th>Net Amount (GBP)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.refunds.map((r) => (
                      <tr key={r.id}>
                        <td>{r.sku}</td>
                        <td>{r.billing_text}</td>
                        <td>{r.quantity}</td>
                        <td>{toGBP(r.return_total)}</td>
                        <td>{toGBP(r.discountedPrice)}</td>
                        <td>{((parseFloat(r.return_total || 0) - parseFloat(r.discountedPrice || 0)) / rate).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Cancelled Items */}
        {order.line_items?.some((i) => i.is_cancelled) && (
          <Card>
            <CardBody>
              <h6 className="mb-3">
                Cancelled Items ({order.line_items.filter((i) => i.is_cancelled).length})
              </h6>
              <div className="table-responsive">
                <Table bordered size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>SKU</th>
                      <th>Gojungo SKU</th>
                      <th>Product</th>
                      <th>Qty</th>
                      <th>Price (GBP)</th>
                      <th>Discount (GBP)</th>
                      <th>Total (GBP)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.line_items.filter((i) => i.is_cancelled).map((item) => (
                      <tr key={item.id}>
                        <td>{item.sku || <span className="text-danger fw-bold">Not Found</span>}</td>
                        <td>{item.gojungo_sku}</td>
                        <td>{item.billing_text}</td>
                        <td>{item.quantity}</td>
                        <td>{toGBP(item.price)}</td>
                        <td>{toGBP(item.discountedPrice)}</td>
                        <td>{(parseFloat(item.price || 0) * parseInt(item.quantity || 0) / rate).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        )}

      </Container>
    </Fragment>
  );
}

export default withReducer([{ order: orderReducer }])(GojungoOrderView);
